/** Parse log `data` from GraphQL (object or JSON string). Does not mutate stored values. */
export const parseMissionLogJson = (data: unknown): Record<string, unknown> | null => {
    if (data && typeof data === "object" && !Array.isArray(data)) {
        return data as Record<string, unknown>;
    }
    if (typeof data !== "string") {
        return null;
    }
    const trimmed = data.trim();
    if (!trimmed.startsWith("{") && !trimmed.startsWith("[")) {
        return null;
    }
    try {
        const parsed: unknown = JSON.parse(trimmed);
        if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
            return parsed as Record<string, unknown>;
        }
    } catch {
        return null;
    }
    return null;
};

/** JSON.stringify replacer: round `time_remaining` for display only. */
const timeRemainingDisplayReplacer = (key: string, value: unknown): unknown => {
    if (key === "time_remaining" && typeof value === "number") {
        return Math.round(value);
    }
    return value;
};

/** Format log/feedback JSON for UI; stored data is never modified. */
export const formatMissionLogDataForDisplay = (data: unknown): string => {
    const record = parseMissionLogJson(data);
    if (!record) {
        return typeof data === "string" ? data : "";
    }
    if (typeof record.time_remaining === "number" && Object.keys(record).length === 1) {
        return `time_remaining: ${Math.round(record.time_remaining)}s`;
    }
    return JSON.stringify(record, timeRemainingDisplayReplacer);
};

/** Format feedback JSON string from mission execution step status. */
export const formatMissionFeedbackForDisplay = (raw: string): string => {
    const trimmed = raw.trim();
    if (!trimmed) {
        return raw;
    }
    const record = parseMissionLogJson(trimmed);
    if (!record) {
        return raw;
    }
    return formatMissionLogDataForDisplay(record);
};

export const buildMissionLogPayloadSummary = (data: unknown, pose: unknown): string => {
    const record = parseMissionLogJson(data);
    if (record) {
        if (typeof record.time_remaining === "number") {
            return `time_remaining: ${Math.round(record.time_remaining)}s`;
        }
        if (typeof record.distance_remaining === "number") {
            return `distance_remaining: ${record.distance_remaining.toFixed(3)}`;
        }
        if (record.result !== undefined) {
            return `result: ${JSON.stringify(record.result)}`;
        }
        if (record.header && typeof record.header === "object") {
            const frameId = (record.header as Record<string, unknown>).frame_id;
            if (typeof frameId === "string") {
                return `frame_id: ${frameId}`;
            }
        }
    }
    if (pose && typeof pose === "object") {
        return "pose captured";
    }
    return "";
};

export interface DetectionHit {
    class_id: string;
    score: number;
}

/** Extract detection results from a serialized DetectionResult log data dict. */
export const extractDetectionHits = (data: unknown): DetectionHit[] => {
    const parsed = parseMissionLogJson(data);
    if (!parsed) return [];
    const wrapper = parsed.detections;
    if (!wrapper || typeof wrapper !== "object" || Array.isArray(wrapper)) return [];
    const detList = (wrapper as Record<string, unknown>).detections;
    if (!Array.isArray(detList)) return [];
    const hits: DetectionHit[] = [];
    for (const det of detList) {
        const results = (det as Record<string, unknown>).results;
        if (!Array.isArray(results)) continue;
        for (const r of results) {
            const hyp = (r as Record<string, unknown>).hypothesis as Record<string, unknown> | undefined;
            if (!hyp) continue;
            const class_id = hyp.class_id;
            const score = hyp.score;
            if (typeof class_id === "string" && typeof score === "number") {
                hits.push({ class_id, score });
            }
        }
    }
    return hits;
};

export const formatMissionLogPayloadForDisplay = (data: unknown, pose: unknown): string => {
    const summary = buildMissionLogPayloadSummary(data, pose);
    if (summary) {
        return summary;
    }
    return formatMissionLogDataForDisplay(data);
};
